"use client";

import { useState, useEffect } from "react";
import { ECGImage } from "@/app/components/ecg-image";
import { Badge } from "@/app/components/badge";
import type { ECGRecord, SCPCode } from "@/app/lib/types";

export default function QuizPage() {
  const [record, setRecord] = useState<ECGRecord | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(true);

  const loadRandom = async () => {
    setLoading(true);
    setShowAnswer(false);
    setAnswer("");
    try {
      const res = await fetch("/api/quiz/random");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecord(data);
    } catch (e) {
      console.error(e);
      setRecord(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRandom(); }, []);

  const positiveCodes = record?.scp_codes.filter((c: SCPCode) => c.confidence > 0) ?? [];

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      <div className="border-b bg-gray-50/50">
        <a href="/" className="block px-6 py-1 text-[11px] text-gray-400 hover:text-gray-600">&larr; Back to main</a>
      </div>
      <header className="border-b px-6 py-3 flex items-stretch gap-6 flex-shrink-0 min-h-0">
        <div className={`flex-1 min-w-0 flex flex-col ${showAnswer ? "" : "invisible"}`}>
          {record && (
            <>
              <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Diagnosis</h3>
                {record.diagnostic_superclass.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {record.diagnostic_superclass.map((d: string) => (
                      <Badge key={d} label={d} type={d} />
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">None</span>
                )}
              </div>

              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex-shrink-0">SCP Codes ({positiveCodes.length})</h3>
              <div className="columns-2 gap-4 space-y-1.5">
                {positiveCodes.map((s: SCPCode) => (
                  <div key={s.code} className="text-xs leading-snug break-inside-avoid">
                    <span className="font-medium text-gray-800">{s.code}</span>
                    <span className="text-gray-400"> — </span>
                    <span className="text-gray-600">{s.description}</span>
                    {s.confidence < 100 && <span className="text-gray-400 ml-1">({s.confidence})</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {record?.report && (
          <div className={`flex-1 min-w-0 border-l pl-4 flex flex-col ${showAnswer ? "" : "invisible"}`}>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2 flex-shrink-0">
              <span>Report</span>
              <button
                onClick={() => setShowOriginal(!showOriginal)}
                className="text-[10px] text-gray-400 hover:text-gray-600 underline cursor-pointer"
              >
                [{showOriginal ? "cleansed" : "original"}]
              </button>
            </h3>
            <div className="flex-1 text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded border overflow-y-auto">
              {showOriginal ? record.report : (record.report_cleansed || record.report)}
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex-shrink-0">Your Answer</h3>
          <textarea
            className="w-full flex-1 border rounded p-3 text-sm resize-none"
            placeholder="Enter your diagnosis..."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={showAnswer}
          />
        </div>

        <div className="flex-shrink-0 flex flex-col justify-end items-end gap-2">
          {record && record.source && (
            <span className="group relative text-xs text-gray-400 cursor-help">
              Source ({record.source.name})
              <span className="absolute top-full right-0 mt-1.5 w-64 px-3 py-2 rounded bg-gray-800 text-white text-[10px] leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                {record.source.citation}.{" "}
                {record.source.url && (
                  <a href={record.source.url} className="underline text-blue-300" target="_blank" rel="noopener noreferrer">{record.source.url}</a>
                )}.{" "}
                {record.source.license && (
                  <>Used under <a href={`https://creativecommons.org/licenses/by/4.0/`} className="underline text-blue-300" target="_blank" rel="noopener noreferrer">{record.source.license}</a>.</>
                )}
                <span className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-800" />
              </span>
            </span>
          )}
          <button
            onClick={showAnswer ? loadRandom : () => setShowAnswer(true)}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? "Loading..." : showAnswer ? "Next" : "Show Report"}
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 p-6">
        {loading && (
          <div className="h-full flex items-center justify-center">
            <div className="animate-pulse text-sm text-gray-500">Loading...</div>
          </div>
        )}
        {!loading && !record && (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <p className="text-sm mb-4">Failed to load a quiz record.</p>
            <button onClick={loadRandom} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Try again
            </button>
          </div>
        )}
        {record && <ECGImage key={record.ecg_id} ecgId={record.ecg_id} format="svg" />}
      </div>
    </div>
  );
}
